/**
 * A synchronous view onto the visitor ID, for callers that cannot await.
 *
 * `fingerprintService.getFingerprint()` is async by contract and `AxiosService`
 * already awaits it — that stays as it is. But `$.ajaxPrefilter` is not async,
 * and neither is a `fetch` call site inside a `pagehide` handler, so those
 * cannot await anything. This module bridges the two: `primeFingerprint()` runs
 * once at boot and fills the store; `getPrimedFingerprint()` reads it
 * synchronously and returns `null` until it lands.
 *
 * The store is **one property on `window`**, not a module-scope variable, and
 * that is load-bearing. Nothing in `config/webpack/` calls `splitChunks`, so a
 * small util imported by several packs is *duplicated* into each entry chunk —
 * `csrfToken.js` already sits in four of them. Priming happens once, in
 * `packs/header_components.js`; every other attach point (`api/index.js`'s
 * per-call instance, the two raw `fetch` sites) lives in a different chunk and
 * would therefore read its *own* copy of a module-scope variable, which nothing
 * ever primes — null forever, no error, and a Jest suite that shares one module
 * registry cannot see it. One global crosses the chunk boundary; a module
 * variable does not.
 *
 * The consequence is deliberate: a cold browser's first request goes out with no
 * header. Holding a request until the ~100–500 ms compute resolves would add
 * latency and a failure mode to every page, which is exactly the trade this
 * project refuses. A NULL is a weak signal; a delayed login is a real cost.
 *
 * Never call `fingerprintService.refreshFingerprint()` from here — a forced
 * recompute re-arms that first-visit race on every page load, turning a rare
 * NULL into a common one.
 *
 * Every failure path resolves to `null`. Nothing here ever throws into a caller.
 */
import fingerprintService from '../services/fingerprintService'
import { isInternalRequest } from './isInternalRequest'

export const FINGERPRINT_HEADER = 'X-Fingerprint'

/**
 * The one place the primed value lives, shared across pack chunks.
 * Namespaced because it sits on `window` alongside everything else a Rails page
 * puts there.
 */
export const FINGERPRINT_GLOBAL_KEY = '__spectoraPrimedFingerprint'

/** @returns {Object|null} `window`, or null where there is none. */
const globalScope = () => (typeof window === 'undefined' ? null : window)

/**
 * Read the shared value, tolerating anything a page may have put on the key.
 *
 * @returns {string|null} The primed value, or null.
 */
const readShared = () => {
  try {
    const scope = globalScope()
    if (!scope) return null

    const value = scope[FINGERPRINT_GLOBAL_KEY]
    return typeof value === 'string' && value ? value : null
  } catch (error) {
    return null
  }
}

/**
 * Write the shared value. A frozen or hostile `window` is not an error here —
 * the page simply stays headerless.
 *
 * @param {string|null} value - The primed value, or null to clear it.
 */
const writeShared = (value) => {
  try {
    const scope = globalScope()
    if (scope) scope[FINGERPRINT_GLOBAL_KEY] = value
  } catch (error) {
    // Ignored on purpose: telemetry must never break a page.
  }
}

// Per-chunk, and deliberately so: only the priming chunk ever calls
// `primeFingerprint()`, so the in-flight promise has nothing to share.
let inFlight = null

/**
 * Compute (or read from cache) the visitor ID and hold it in module scope.
 *
 * Concurrent callers share one in-flight promise, so N attach points priming on
 * the same page produce one compute rather than N. A failure clears the promise
 * so a later call retries instead of inheriting the first failure for the whole
 * session.
 *
 * @returns {Promise<string|null>} The primed value, or null on any failure.
 */
export const primeFingerprint = () => {
  const shared = readShared()
  if (shared) return Promise.resolve(shared)
  if (inFlight) return inFlight

  inFlight = Promise.resolve()
    .then(() => fingerprintService.getFingerprint())
    .then((value) => {
      writeShared(value || null)
      inFlight = null
      return readShared()
    })
    .catch(() => {
      // Cleared rather than remembered, so a transient failure does not make the
      // page permanently headerless.
      inFlight = null
      return null
    })

  return inFlight
}

/**
 * Read the primed visitor ID without awaiting anything.
 *
 * Reads the shared store, so a caller in any pack chunk sees the value primed
 * by `packs/header_components.js`.
 *
 * @returns {string|null} The primed value, or null when it has not landed yet.
 */
export const getPrimedFingerprint = () => readShared()

/**
 * Build the fingerprint header for a request, or an empty object.
 *
 * The single place the origin gate and the holder are combined, so no attach
 * point can accidentally apply one without the other. An external host, a
 * malformed URL, or an unresolved fingerprint all yield `{}` — never a
 * placeholder value.
 *
 * @param {string} url - Request URL, absolute or relative.
 * @param {string} [baseURL] - Axios-style base URL, only used for relative URLs.
 * @returns {Object} `{ 'X-Fingerprint': value }` or `{}`.
 */
export const fingerprintHeadersFor = (url, baseURL) => {
  try {
    if (!isInternalRequest(url, baseURL)) return {}

    const value = getPrimedFingerprint()
    if (!value) return {}

    return { [FINGERPRINT_HEADER]: value }
  } catch (error) {
    // Fail closed and silently: telemetry must never break a request.
    return {}
  }
}

/** Reset module state. Test-only. */
export const resetFingerprintHolder = () => {
  writeShared(null)
  inFlight = null
}
